'use client'

import { useEffect, useRef } from 'react'

import { useSession } from 'next-auth/react'

import { env } from '@/env'
import {
  ACTIVITY_EVENTS,
  FLUSH_INTERVAL_SECONDS,
  IDLE_TIMEOUT_SECONDS,
  MAX_SECONDS_PER_FLUSH,
  TICK_INTERVAL_MS,
  USAGE_TIME_ENDPOINT
} from '@/shared/lib/usage-time'

/**
 * Contabiliza o tempo ativo do aluno na aplicação e o reporta periodicamente.
 *
 * Invisível para o aluno: o hook não renderiza nada e não expõe estado.
 *
 * A contagem é feita por um tick de 1s que só incrementa quando a aba está
 * visível e houve interação nos últimos IDLE_TIMEOUT_SECONDS. Somar segundos
 * enquanto o estado é ativo — em vez de marcar início e fim de cada período —
 * torna a contagem imune a bordas perdidas: se o estado ficar errado, o erro é
 * de um segundo por tick, não de uma sessão inteira.
 */
export function useUsageTimeTracker() {
  const { data: session } = useSession()

  // O token é espelhado num ref porque o flush de unload precisa montar a
  // requisição de forma síncrona — não há tempo para um getSession() async.
  const tokenRef = useRef<string | undefined>(undefined)
  const lastInteractionRef = useRef<number>(Date.now())
  const unsentSecondsRef = useRef<number>(0)
  const inFlightRef = useRef<boolean>(false)

  useEffect(() => {
    tokenRef.current = (session as { accessToken?: string } | null)?.accessToken
  }, [session])

  useEffect(() => {
    if (typeof window === 'undefined') return

    // Envia até MAX_SECONDS_PER_FLUSH e devolve o saldo ao acumulador se a
    // requisição falhar, para que o tempo entre no envio seguinte em vez de se
    // perder. A subtração otimista antes do await evita que os ticks ocorridos
    // durante a requisição sejam contados duas vezes.
    async function flush() {
      const token = tokenRef.current
      if (token === undefined) return
      if (inFlightRef.current) return

      const amount = Math.min(unsentSecondsRef.current, MAX_SECONDS_PER_FLUSH)
      if (amount <= 0) return

      unsentSecondsRef.current -= amount
      inFlightRef.current = true

      try {
        await fetch(`${env.NEXT_PUBLIC_PGEE_API_BASE_URL}${USAGE_TIME_ENDPOINT}`, {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ seconds: amount })
        })
      } catch {
        // Falha de rede não interrompe a experiência do aluno nem exibe erro:
        // o tempo volta para o acumulador e tenta de novo no próximo flush.
        unsentSecondsRef.current += amount
      } finally {
        inFlightRef.current = false
      }
    }

    function registerInteraction() {
      lastInteractionRef.current = Date.now()
    }

    function isActive() {
      if (document.visibilityState !== 'visible') return false

      const idleFor = (Date.now() - lastInteractionRef.current) / 1000
      return idleFor < IDLE_TIMEOUT_SECONDS
    }

    const tick = setInterval(() => {
      if (!isActive()) return

      unsentSecondsRef.current += TICK_INTERVAL_MS / 1000

      if (unsentSecondsRef.current >= FLUSH_INTERVAL_SECONDS) flush()
    }, TICK_INTERVAL_MS)

    function onVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        flush()
        return
      }

      // Ao voltar, a contagem só recomeça com nova interação: não creditamos
      // retroativamente o tempo em que a aba esteve oculta.
      registerInteraction()
    }

    // Os listeners só escrevem um timestamp num ref — sem estado, sem re-render
    // e sem trabalho por evento, então não precisam de throttle adicional.
    ACTIVITY_EVENTS.forEach(event =>
      window.addEventListener(event, registerInteraction, { passive: true })
    )
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pagehide', flush)

    return () => {
      clearInterval(tick)
      ACTIVITY_EVENTS.forEach(event =>
        window.removeEventListener(event, registerInteraction)
      )
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('pagehide', flush)
      flush()
    }
  }, [])
}
